import React, { useState, useMemo } from 'react';
import './LeaveCalendar.css';

// ═══════════════════════════════════════════════════════════════
//  DOE 2025-2026 School Year Calendar Data
// ═══════════════════════════════════════════════════════════════

const SCHOOL_YEAR = '2025-2026';

// Format: 'YYYY-MM-DD'
const DOE_EVENTS = {
  // ═══════════════════════════════════════════════════════════
  //  Official NYC DOE 2025-2026 School Year Calendar
  //  Source: schools.nyc.gov/calendar/2025-2026-school-year-calendar
  // ═══════════════════════════════════════════════════════════

  // ── School year milestones ──
  '2025-09-04': { type: 'start', label: 'First Day of School' },
  '2026-06-26': { type: 'end', label: 'Last Day of School' },

  // ── DOE Holidays (schools closed) ──
  '2025-09-23': { type: 'holiday', label: 'Rosh Hashanah' },
  '2025-09-24': { type: 'holiday', label: 'Rosh Hashanah' },
  '2025-10-02': { type: 'holiday', label: 'Yom Kippur' },
  '2025-10-13': { type: 'holiday', label: 'Italian Heritage / Indigenous Peoples\' Day' },
  '2025-10-20': { type: 'holiday', label: 'Diwali' },
  '2025-11-11': { type: 'holiday', label: 'Veterans Day' },
  '2025-11-27': { type: 'holiday', label: 'Thanksgiving Recess' },
  '2025-11-28': { type: 'holiday', label: 'Thanksgiving Recess' },
  '2025-12-24': { type: 'holiday', label: 'Winter Recess' },
  '2025-12-25': { type: 'holiday', label: 'Winter Recess (Christmas)' },
  '2025-12-26': { type: 'holiday', label: 'Winter Recess' },
  '2025-12-29': { type: 'holiday', label: 'Winter Recess' },
  '2025-12-30': { type: 'holiday', label: 'Winter Recess' },
  '2025-12-31': { type: 'holiday', label: 'Winter Recess (New Year\'s Eve)' },
  '2026-01-01': { type: 'holiday', label: 'Winter Recess (New Year\'s Day)' },
  '2026-01-02': { type: 'holiday', label: 'Winter Recess' },
  '2026-01-19': { type: 'holiday', label: 'Rev. Dr. Martin Luther King Jr. Day' },
  '2026-02-16': { type: 'holiday', label: 'Midwinter Recess' },
  '2026-02-17': { type: 'holiday', label: 'Midwinter Recess' },
  '2026-02-18': { type: 'holiday', label: 'Midwinter Recess' },
  '2026-02-19': { type: 'holiday', label: 'Midwinter Recess' },
  '2026-02-20': { type: 'holiday', label: 'Midwinter Recess' },
  '2026-03-20': { type: 'holiday', label: 'Eid al-Fitr' },
  '2026-04-02': { type: 'holiday', label: 'Spring Recess' },
  '2026-04-03': { type: 'holiday', label: 'Spring Recess (Good Friday)' },
  '2026-04-06': { type: 'holiday', label: 'Spring Recess' },
  '2026-04-07': { type: 'holiday', label: 'Spring Recess' },
  '2026-04-08': { type: 'holiday', label: 'Spring Recess' },
  '2026-04-09': { type: 'holiday', label: 'Spring Recess' },
  '2026-04-10': { type: 'holiday', label: 'Spring Recess' },
  '2026-05-25': { type: 'holiday', label: 'Memorial Day' },
  '2026-05-27': { type: 'holiday', label: 'Eid al-Adha' },
  '2026-06-04': { type: 'holiday', label: 'Anniversary Day / Clerical Day' },

  // ── No School / Special Days (not holidays but students don't attend) ──
  '2025-11-04': { type: 'half', label: 'Election Day — students do not attend' },
  '2026-01-26': { type: 'half', label: 'PD Day (HS & 6-12 only) — no students' },
  '2026-06-05': { type: 'half', label: 'Chancellor\'s Conference Day — no students' },

  // ── Regents Exam Periods ──
  '2026-01-20': { type: 'regents', label: 'Regents Exams' },
  '2026-01-21': { type: 'regents', label: 'Regents Exams' },
  '2026-01-22': { type: 'regents', label: 'Regents Exams' },
  '2026-01-23': { type: 'regents', label: 'Regents Exams' },
  '2026-06-17': { type: 'regents', label: 'Regents Exams' },
  '2026-06-18': { type: 'regents', label: 'Regents Exams' },
  '2026-06-19': { type: 'regents', label: 'Regents Exams (Juneteenth)' },
  '2026-06-22': { type: 'regents', label: 'Regents Exams' },
  '2026-06-23': { type: 'regents', label: 'Regents Exams' },
  '2026-06-24': { type: 'regents', label: 'Regents Exams' },
  '2026-06-25': { type: 'regents', label: 'Regents Rating Day' },

  // ── Parent-Teacher Conferences ──
  // Fall Round 1 - Evening only
  '2025-09-17': { type: 'conference', label: 'PT Conference Evening (Elementary & Pre-K)' },
  '2025-09-18': { type: 'conference', label: 'PT Conference Evening (Middle & D75)' },
  '2025-09-25': { type: 'conference', label: 'PT Conference Evening (High School & K-12)' },
  // Fall Round 2 - Afternoon + Evening (early dismissal)
  '2025-11-06': { type: 'conference', label: 'PT Conference (Elementary) — early dismissal' },
  '2025-11-13': { type: 'conference', label: 'PT Conference (Middle & D75) — early dismissal' },
  '2025-11-20': { type: 'conference', label: 'PT Conference Evening (High School & K-12)' },
  '2025-11-21': { type: 'conference', label: 'PT Conference (High School) — early dismissal' },
  // Spring Round 1
  '2026-03-05': { type: 'conference', label: 'PT Conference (Elementary) — early dismissal' },
  '2026-03-12': { type: 'conference', label: 'PT Conference (Middle & D75) — early dismissal' },
  '2026-03-18': { type: 'conference', label: 'PT Conference Evening (High School & K-12)' },
  '2026-03-19': { type: 'conference', label: 'PT Conference (High School) — early dismissal' },
  // Spring Round 2 - Evening only
  '2026-05-06': { type: 'conference', label: 'PT Conference Evening (Middle & D75)' },
  '2026-05-07': { type: 'conference', label: 'PT Conference Evening (Elementary & Pre-K)' },
  '2026-05-20': { type: 'conference', label: 'PT Conference Evening (High School & K-12)' },
};

// ── Leave types and CAR info ──
const LEAVE_TYPES = {
  maternity: {
    title: 'Maternity / Childbirth Leave',
    description: 'Use CAR days for up to 6-8 weeks recovery, then FMLA runs concurrently. Total FMLA = 12 weeks.',
    phases: [
      { name: 'Pre-Delivery Prep', weeks: 0, color: '#f59e0b', note: 'Use sick days as needed before due date' },
      { name: 'Childbirth Recovery (CAR)', weeks: 6, color: '#ef4444', note: '6 weeks vaginal / 8 weeks C-section — paid via CAR days' },
      { name: 'FMLA Bonding (unpaid)', weeks: 6, color: '#7c3aed', note: 'Remaining FMLA weeks — unpaid but job-protected' },
      { name: 'Child Care Leave (optional)', weeks: 0, color: '#2152ff', note: 'Up to 4 years unpaid — must apply separately' },
    ],
  },
  fmla: {
    title: 'FMLA Leave',
    description: '12 weeks of job-protected leave per school year. Can be continuous or intermittent.',
    phases: [
      { name: 'FMLA Leave', weeks: 12, color: '#7c3aed', note: '12 weeks — use CAR for paid portion, unpaid after' },
    ],
  },
  car: {
    title: 'CAR (Cumulative Absence Reserve)',
    description: 'You earn 1 sick day per month (10/year). Unused days accumulate in your CAR bank with no cap.',
    phases: [],
  },
  paternity: {
    title: 'Paternity / Non-Birthing Parent Leave',
    description: 'FMLA provides 12 weeks job-protected leave for bonding. Use CAR days for paid time off.',
    phases: [
      { name: 'FMLA Bonding Leave', weeks: 12, color: '#7c3aed', note: '12 weeks job-protected — paid via CAR days if available, unpaid otherwise' },
    ],
  },
};

// ═══════════════════════════════════════════════════════════════
//  Helpers
// ═══════════════════════════════════════════════════════════════

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAYS = ['Su','Mo','Tu','We','Th','Fr','Sa'];

function daysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function firstDayOfMonth(year, month) {
  return new Date(year, month, 1).getDay();
}

function dateKey(year, month, day) {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function addDays(dateStr, days) {
  const d = new Date(dateStr + 'T00:00:00');
  d.setDate(d.getDate() + days);
  return dateKey(d.getFullYear(), d.getMonth(), d.getDate());
}

function isWeekday(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  const day = d.getDay();
  return day !== 0 && day !== 6;
}

function formatDate(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ═══════════════════════════════════════════════════════════════
//  LeaveCalendar Component
// ═══════════════════════════════════════════════════════════════

export default function LeaveCalendar({ widget = {} }) {
  const { type = 'school_calendar', leave_type, suggested_date } = widget;

  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [selectedDate, setSelectedDate] = useState(null);
  const [activeTab, setActiveTab] = useState(type === 'leave_planner' ? 'planner' : 'calendar');
  const [leaveStart, setLeaveStart] = useState(suggested_date || null);
  const [selectedLeaveType, setSelectedLeaveType] = useState(leave_type || 'maternity');
  const [cSection, setCSection] = useState(false);

  // ── Navigation ──
  const prevMonth = () => {
    if (currentMonth === 0) { setCurrentMonth(11); setCurrentYear(y => y - 1); }
    else setCurrentMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (currentMonth === 11) { setCurrentMonth(0); setCurrentYear(y => y + 1); }
    else setCurrentMonth(m => m + 1);
  };
  const goToToday = () => { setCurrentMonth(today.getMonth()); setCurrentYear(today.getFullYear()); };

  // ── Leave timeline calculation ──
  const leaveTimeline = useMemo(() => {
    if (!leaveStart) return null;
    const info = LEAVE_TYPES[selectedLeaveType];
    if (!info || info.phases.length === 0) return null;

    let phases = [...info.phases];
    // Adjust maternity for C-section
    if (selectedLeaveType === 'maternity' && cSection) {
      phases = phases.map(p =>
        p.name.includes('Childbirth Recovery') ? { ...p, weeks: 8 } : p
      );
    }

    const timeline = [];
    let currentDate = leaveStart;
    for (const phase of phases) {
      if (phase.weeks === 0) continue;
      const endDate = addDays(currentDate, phase.weeks * 7 - 1);
      timeline.push({ ...phase, start: currentDate, end: endDate });
      currentDate = addDays(endDate, 1);
    }
    return timeline;
  }, [leaveStart, selectedLeaveType, cSection]);

  // Build set of leave dates for calendar highlighting
  const leaveDates = useMemo(() => {
    if (!leaveTimeline) return {};
    const dates = {};
    for (const phase of leaveTimeline) {
      let d = phase.start;
      while (d <= phase.end) {
        dates[d] = { color: phase.color, name: phase.name };
        d = addDays(d, 1);
      }
    }
    return dates;
  }, [leaveTimeline]);

  // ── Build calendar grid ──
  const calendarDays = useMemo(() => {
    const total = daysInMonth(currentYear, currentMonth);
    const startDay = firstDayOfMonth(currentYear, currentMonth);
    const days = [];

    // Empty slots
    for (let i = 0; i < startDay; i++) days.push(null);
    // Days
    for (let d = 1; d <= total; d++) days.push(d);

    return days;
  }, [currentYear, currentMonth]);

  // ── Date click handler ──
  const handleDateClick = (day) => {
    if (!day) return;
    const key = dateKey(currentYear, currentMonth, day);
    if (activeTab === 'planner') {
      setLeaveStart(key);
      setSelectedDate(null);
    } else {
      setSelectedDate(selectedDate === key ? null : key);
    }
  };

  // ── Get event classes for a day ──
  const getDayInfo = (day) => {
    if (!day) return { classes: '', event: null, leave: null };
    const key = dateKey(currentYear, currentMonth, day);
    const event = DOE_EVENTS[key];
    const leave = leaveDates[key];
    const isToday = key === dateKey(today.getFullYear(), today.getMonth(), today.getDate());
    const isSelected = key === selectedDate;
    const isLeaveStart = key === leaveStart;

    let classes = '';
    if (isToday) classes += ' today';
    if (isSelected) classes += ' selected';
    if (isLeaveStart) classes += ' leave-start';
    if (event) classes += ` event-${event.type}`;
    if (leave) classes += ' on-leave';
    if (!isWeekday(key)) classes += ' weekend';

    return { classes, event, leave, key };
  };

  // ── Count upcoming events ──
  const upcomingEvents = useMemo(() => {
    const todayKey = dateKey(today.getFullYear(), today.getMonth(), today.getDate());
    return Object.entries(DOE_EVENTS)
      .filter(([d]) => d >= todayKey)
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(0, 5);
  }, []);

  // ── Count school days left ──
  const schoolDaysLeft = useMemo(() => {
    const todayKey = dateKey(today.getFullYear(), today.getMonth(), today.getDate());
    const endKey = '2026-06-26';
    let count = 0;
    let d = todayKey;
    while (d <= endKey) {
      if (isWeekday(d) && !DOE_EVENTS[d]?.type?.match(/holiday/)) count++;
      d = addDays(d, 1);
    }
    return count;
  }, []);

  return (
    <div className="leave-calendar-widget">
      {/* ── Tabs ── */}
      <div className="lc-tabs">
        <button
          className={`lc-tab ${activeTab === 'calendar' ? 'active' : ''}`}
          onClick={() => setActiveTab('calendar')}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
          </svg>
          School Year
        </button>
        <button
          className={`lc-tab ${activeTab === 'planner' ? 'active' : ''}`}
          onClick={() => setActiveTab('planner')}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 22c5.5-3 8.5-7 8.5-11.5C20.5 5.8 16.7 2 12 2S3.5 5.8 3.5 10.5C3.5 15 6.5 19 12 22z"/>
            <circle cx="12" cy="10" r="3"/>
          </svg>
          Leave Planner
        </button>
      </div>

      {/* ── Calendar Header ── */}
      <div className="lc-header">
        <button className="lc-nav" onClick={prevMonth}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
        <div className="lc-month-year">
          <span className="lc-month">{MONTHS[currentMonth]}</span>
          <span className="lc-year">{currentYear}</span>
        </div>
        <button className="lc-nav" onClick={nextMonth}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="9 6 15 12 9 18"/></svg>
        </button>
        <button className="lc-today-btn" onClick={goToToday}>Today</button>
      </div>

      {/* ── Day Headers ── */}
      <div className="lc-day-headers">
        {DAYS.map(d => <div key={d} className="lc-day-header">{d}</div>)}
      </div>

      {/* ── Calendar Grid ── */}
      <div className="lc-grid">
        {calendarDays.map((day, i) => {
          const { classes, event, leave, key } = getDayInfo(day);
          return (
            <div
              key={i}
              className={`lc-day${day ? '' : ' empty'}${classes}`}
              onClick={() => handleDateClick(day)}
              style={leave ? { '--leave-color': leave.color } : undefined}
            >
              {day && (
                <>
                  <span className="lc-day-num">{day}</span>
                  {event && <div className="lc-dot" />}
                  {leave && <div className="lc-leave-bar" style={{ background: leave.color }} />}
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Selected Date Detail ── */}
      {selectedDate && DOE_EVENTS[selectedDate] && (
        <div className={`lc-detail event-${DOE_EVENTS[selectedDate].type}`}>
          <div className="lc-detail-date">{formatDate(selectedDate)}</div>
          <div className="lc-detail-label">{DOE_EVENTS[selectedDate].label}</div>
          <div className="lc-detail-type">{DOE_EVENTS[selectedDate].type.replace('_', ' ')}</div>
        </div>
      )}

      {/* ── Calendar Tab: Legend + Upcoming ── */}
      {activeTab === 'calendar' && (
        <div className="lc-info-section">
          <div className="lc-legend">
            <div className="lc-legend-item"><span className="lc-legend-dot holiday" /> Holiday / No School</div>
            <div className="lc-legend-item"><span className="lc-legend-dot regents" /> Regents Exams</div>
            <div className="lc-legend-item"><span className="lc-legend-dot half" /> Half Day / PD</div>
            <div className="lc-legend-item"><span className="lc-legend-dot conference" /> Parent-Teacher Conf.</div>
            <div className="lc-legend-item"><span className="lc-legend-dot start" /> First / Last Day</div>
          </div>

          <div className="lc-upcoming">
            <div className="lc-upcoming-header">
              <span>📅 Coming Up</span>
              <span className="lc-school-days">{schoolDaysLeft} school days left</span>
            </div>
            {upcomingEvents.map(([date, evt]) => (
              <div key={date} className="lc-upcoming-item" onClick={() => {
                const d = new Date(date + 'T00:00:00');
                setCurrentMonth(d.getMonth());
                setCurrentYear(d.getFullYear());
                setSelectedDate(date);
              }}>
                <span className="lc-upcoming-date">{formatDate(date)}</span>
                <span className="lc-upcoming-label">{evt.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Planner Tab: Leave Type Selector + Timeline ── */}
      {activeTab === 'planner' && (
        <div className="lc-planner-section">
          <div className="lc-planner-controls">
            <label className="lc-select-label">Leave Type</label>
            <div className="lc-select-group">
              {Object.entries(LEAVE_TYPES).map(([key, info]) => (
                <button
                  key={key}
                  className={`lc-leave-btn ${selectedLeaveType === key ? 'active' : ''}`}
                  onClick={() => setSelectedLeaveType(key)}
                >
                  {info.title.split('/')[0].trim().replace('Maternity', 'Maternity').replace('Cumulative Absence Reserve', 'CAR Days')}
                </button>
              ))}
            </div>

            {selectedLeaveType === 'maternity' && (
              <label className="lc-checkbox">
                <input type="checkbox" checked={cSection} onChange={e => setCSection(e.target.checked)} />
                <span>C-section (8 weeks recovery instead of 6)</span>
              </label>
            )}

            <p className="lc-planner-desc">{LEAVE_TYPES[selectedLeaveType].description}</p>

            {LEAVE_TYPES[selectedLeaveType].phases.length > 0 && (
              <div className="lc-start-prompt">
                {leaveStart ? (
                  <div className="lc-start-chosen">
                    <span>Leave starts:</span>
                    <strong>{formatDate(leaveStart)}</strong>
                    <button className="lc-clear-btn" onClick={() => setLeaveStart(null)}>✕</button>
                  </div>
                ) : (
                  <p className="lc-click-hint">👆 Click a date on the calendar to set your leave start</p>
                )}
              </div>
            )}
          </div>

          {/* ── Timeline visualization ── */}
          {leaveTimeline && leaveTimeline.length > 0 && (
            <div className="lc-timeline">
              <div className="lc-timeline-header">Your Leave Timeline</div>
              {leaveTimeline.map((phase, i) => (
                <div key={i} className="lc-timeline-phase">
                  <div className="lc-timeline-bar" style={{ background: phase.color }}>
                    <span className="lc-timeline-name">{phase.name}</span>
                    <span className="lc-timeline-weeks">{phase.weeks}wk</span>
                  </div>
                  <div className="lc-timeline-dates">
                    {formatDate(phase.start)} — {formatDate(phase.end)}
                  </div>
                  <div className="lc-timeline-note">{phase.note}</div>
                </div>
              ))}
              <div className="lc-timeline-total">
                <span>Total leave:</span>
                <strong>{leaveTimeline.reduce((sum, p) => sum + p.weeks, 0)} weeks</strong>
                <span className="lc-timeline-return">
                  Return: {formatDate(addDays(leaveTimeline[leaveTimeline.length - 1].end, 1))}
                </span>
              </div>
            </div>
          )}

          {/* ── CAR day info ── */}
          {selectedLeaveType === 'car' && (
            <div className="lc-car-info">
              <div className="lc-car-title">📋 CAR Day Quick Facts</div>
              <div className="lc-car-grid">
                <div className="lc-car-item">
                  <div className="lc-car-num">10</div>
                  <div className="lc-car-label">Days earned per year</div>
                </div>
                <div className="lc-car-item">
                  <div className="lc-car-num">∞</div>
                  <div className="lc-car-label">No cap on accumulation</div>
                </div>
                <div className="lc-car-item">
                  <div className="lc-car-num">1/mo</div>
                  <div className="lc-car-label">Earned Sept → June</div>
                </div>
                <div className="lc-car-item">
                  <div className="lc-car-num">$$</div>
                  <div className="lc-car-label">Paid out at retirement</div>
                </div>
              </div>
              <div className="lc-car-notes">
                <p>• Self-treated absences without a doctor's note are limited to <strong>3 consecutive days</strong></p>
                <p>• After 3 days, you need medical documentation</p>
                <p>• Your principal <strong>cannot</strong> require a doctor's note for 1-3 day absences</p>
                <p>• CAR days can be used for personal illness, family illness, or childcare needs</p>
                <p>• Check your CAR balance on DOE Employee Self-Service (ESS)</p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}